import { useMe } from '@/context/me/me.context';
import useQuestionnaireData from '@/hooks/use-questionnaire-data';
import { useGetCommPreferences } from '@/queries/comm-preferences.queries';
import { useGetMembershipStatus } from '@/queries/membership.queries';
import { useGetQuestionnaire } from '@/queries/questionnaire.queries';
import { useGetSubscriptionsV1SubscriptionsGet } from '@/api/generated-catch-backend/subscriptions/subscriptions';

export default function useAccountController() {
  const { data: userData } = useMe();
  const name = userData?.profile?.name as string;
  const email = userData?.account.credential as string;
  const membershipId = userData?.membership.id as string;
  const cardLast4Digits = userData?.membership?.paymentMethod?.last4 ?? '';
  const paymentFunding = userData?.membership?.paymentMethod?.funding ?? '';

  const { data: userQuestionnaire } = useGetQuestionnaire();
  const { questionnaireStatus, iDontKnowAnswers, answeredQuestions, assessmentProgress } =
    useQuestionnaireData(userQuestionnaire);

  const { data: membershipData } = useGetMembershipStatus(membershipId);
  const { data: subscriptionData } = useGetSubscriptionsV1SubscriptionsGet();

  const type = membershipData?.type || '';
  const cancelAtPeriodEnd = membershipData?.cancelAtPeriodEnd ?? false;
  const price = membershipData?.price?.amount ?? 0;
  const usdPrice = price / 100;

  let renewalDate = '';
  if (membershipData?.renewalDate) {
    const date = new Date(membershipData.renewalDate);
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
    renewalDate = new Intl.DateTimeFormat('en-US', options).format(date);
  } else {
    renewalDate = 'N/A';
  }

  // Format subscription status for display (e.g., "active" -> "Active", "past_due" -> "Past Due", "trialing" -> "Trial")
  const formatStatus = (status?: string) => {
    if (!status) return undefined;
    if (status.toLowerCase() === 'trialing') return 'Trial';
    return status
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  const subscriptionStatus = formatStatus(subscriptionData?.status);

  // Simple check: if membership exists and has VIP type, set isVipAccess
  const isVipAccess = type === 'VIP';

  const { data: communicationPreferences } = useGetCommPreferences();

  const notificationsPreferences = communicationPreferences?.preferences || [];

  return {
    name,
    email,
    cardLast4Digits,
    assessmentProgress,
    type,
    cancelAtPeriodEnd,
    usdPrice,
    renewalDate,
    questionnaireStatus,
    iDontKnowAnswers,
    answeredQuestions,
    notificationsPreferences,
    paymentFunding,
    isVipAccess,
    subscriptionStatus,
  };
}
